import { useCallback, useRef } from 'react';
import { PICKABLE_ISSUE_TYPES } from '@overlayqa/shared';

const PICKABLE = new Set(PICKABLE_ISSUE_TYPES.map((t) => t.value as string));

/**
 * The scanner-stamped type each row arrived with, for as long as this page is open.
 *
 * Inline editing saves on the pick, so there is no Cancel. The shipped picker
 * rule keeps a stamped type (A11y, Token, Debt) selectable on the issue that
 * carries it (TicketDetailsCard.tsx:311-316), but it reads the CURRENT value:
 * the moment a row moves from Accessibility to Bug, Accessibility leaves the
 * list and the mis-click is stuck until a reload. Remembering the first stamped
 * type keeps the way back on offer.
 *
 * A ref, not state: this is a record of what was seen, and writing it must not
 * render. It is deliberately not persisted, so a reload is the clean slate.
 * Contract pinned by __tests__/useRememberedIssueTypes.test.tsx.
 */
export function useRememberedIssueTypes(issues: Array<{ id: string; type: string }>) {
  const seen = useRef(new Map<string, string>());

  // During render, not in an effect: the columns are built in the same pass, so
  // a row's first render must already be able to ask. Writing a Map keyed by id
  // is idempotent, which is what makes that safe under StrictMode's double call.
  for (const issue of issues) {
    if (!issue?.id || PICKABLE.has(issue.type)) continue;
    if (!seen.current.has(issue.id)) seen.current.set(issue.id, issue.type);
  }

  return useCallback((issue: { id: string }) => seen.current.get(issue.id), []);
}
